import type { DropdownOption } from 'components/DropdownListWithOptions';
import { TicMetaIcon } from 'components/TicMetaIcon';
import { mapValues, sum, uniq } from 'lodash-es';
import { DateTime, Interval } from 'luxon';
import React from 'react';
import { getDailyBaseCapacityByDate } from 'redux/selectors/utils/capacities';
import type { TaskAnalyticsContext } from 'types/analytics';
import type { RawCapacity } from 'types/summaries';
import type { TicMeta } from 'types/tic';
import type { Merge } from 'types/utility';
import { generateDays } from 'utils/date-helper';
import { durationFromHours } from 'utils/durations';
import { ucFirst } from 'utils/text-helper';
import { enrichTicMeta } from 'utils/tic/enrichTicMeta';
import { dayTools } from 'utils/time';
import { keyBy } from 'utils/typesafe';
import uuidv4 from 'utils/uuid';
import type {
  EnrichedForecast,
  Forecast,
  ForecastAllocation,
  ForecastAssignee,
  ForecastDraft,
} from '../types/forecast';

export function calculateRemainingUnitsOfTime(
  estimatedMinutes: number,
  loggedMinutes: number,
  minutesPerDayRequired: number
) {
  const minutesOfWorkRemaining = estimatedMinutes - loggedMinutes;
  const minutesOfWorkRemainingAsDays =
    minutesPerDayRequired && minutesOfWorkRemaining
      ? minutesOfWorkRemaining / minutesPerDayRequired
      : 0;

  return {
    minutesOfWorkRemaining,
    minutesOfWorkRemainingAsDays,
  };
}

const startOfToday = () => DateTime.now().startOf('day');

export const enrichForecast = (
  forecast: Forecast,
  userCapacityMap: Record<number, Array<RawCapacity>> = {}
): EnrichedForecast => {
  const enrichedForecast = enrichTicMeta(forecast);
  const { estimated_minutes, logged_duration, to, from, completed_at, users } = enrichedForecast;
  const loggedMinutes = logged_duration.total_minutes;
  const today = startOfToday().toISODate();
  const dueInDays = DateTime.fromISO(to).diff(startOfToday(), 'days').days + 1;
  const isAfterStartDate = dayTools.isAfter(today, from);
  const isOverdue = today > to;
  const delayedForDays = isOverdue ? startOfToday().diff(DateTime.fromISO(to), 'days').days : 0;

  const rangeInterval =
    from && to ? Interval.fromDateTimes(DateTime.fromISO(from), DateTime.fromISO(to)) : null;

  // Tasks are split evenly between assignees at the moment
  const estimatedMinutesPerUser = estimated_minutes / (users.length || 1);
  const loggedMinutesAveragePerUser = loggedMinutes / (users.length || 1);

  const allocationByUser = mapValues(
    keyBy(
      users.map(user => {
        return {
          user,
          allocation: calculateUserForecastAllocation({
            user,
            interval: rangeInterval,
            capacityHistory: userCapacityMap[user.id],
            estimatedMinutes: estimatedMinutesPerUser,
            loggedMinutes: loggedMinutesAveragePerUser,
            isAfterStartDate,
            isOverdue,
          }),
        };
      }),
      'user.id'
    ),
    'allocation'
  );

  const progressedPercent =
    loggedMinutes && estimated_minutes
      ? +((loggedMinutes / estimated_minutes) * 100).toFixed(2)
      : 0;

  const plannedPercent =
    forecast.planned_duration.total_minutes && estimated_minutes
      ? +((forecast.planned_duration.total_minutes / estimated_minutes) * 100).toFixed(2)
      : 0;

  const totalMinutesAllocation =
    forecast.planned_duration.total_minutes + logged_duration.total_minutes - estimated_minutes;

  const overtime = totalMinutesAllocation > 0 ? totalMinutesAllocation : null;

  return {
    ...enrichedForecast,
    meta: {
      dueInDays,
      delayedForDays,
      progressedPercent,
      plannedPercent,
      totalEstimatedProgress: plannedPercent + progressedPercent,
      isDone: !!completed_at,
      isOverdue,
      overtime,
    },
    allocationByUser,
  };
};

function calculateUserForecastAllocation({
  user,
  interval,
  capacityHistory = [],
  estimatedMinutes,
  loggedMinutes,
  isAfterStartDate,
  isOverdue,
}: {
  user: ForecastAssignee;
  interval: Interval | null;
  capacityHistory?: Array<RawCapacity>;
  estimatedMinutes: number;
  loggedMinutes: number;
  isAfterStartDate: boolean;
  isOverdue: boolean;
}): ForecastAllocation {
  const capacityMap: Array<{ date: string; baseCapacity: number | null }> = [];

  if (interval) {
    for (const day of generateDays(interval)) {
      const date = day.toISODate();
      const baseCapacity = getDailyBaseCapacityByDate(capacityHistory, date);
      capacityMap.push({ date, baseCapacity });
    }
  }
  const workdaysRange =
    capacityMap.length > 0 ? capacityMap.filter(day => day.baseCapacity != null) : null;
  const rangeHasMultipleCapacities = workdaysRange
    ? uniq(workdaysRange.map(day => day.baseCapacity)).length > 1
    : false;
  const totalCapacityForRange = workdaysRange
    ? Number(sum(workdaysRange.map(({ baseCapacity }) => baseCapacity)))
    : null;
  const remainingDays = Object.entries(capacityMap).filter(
    ([key, value]) => DateTime.fromISO(key) >= startOfToday() && value != null
  );

  const daysRequiredToComplete = workdaysRange?.length || 0;
  const dailyWorkMinutesRequiredToCompleteWithinRange = daysRequiredToComplete
    ? Math.round(estimatedMinutes / daysRequiredToComplete)
    : 0;

  const { minutesOfWorkRemaining, minutesOfWorkRemainingAsDays } = calculateRemainingUnitsOfTime(
    estimatedMinutes,
    loggedMinutes,
    dailyWorkMinutesRequiredToCompleteWithinRange
  );

  const workdaysToInitialDueDate = isAfterStartDate && !isOverdue ? remainingDays.length : null;

  const dailyWorkMinutesRequiredToCompleteByInitialDueDate = workdaysToInitialDueDate
    ? minutesOfWorkRemaining / workdaysToInitialDueDate
    : 0;

  const totalWorkdays = user.work_days?.split(',').length || 5;

  const currentDailyUserCapacity =
    !rangeHasMultipleCapacities && user.weekly_capacity
      ? user.weekly_capacity / totalWorkdays
      : null;

  const averageDailyCapacity =
    rangeHasMultipleCapacities && totalCapacityForRange
      ? totalCapacityForRange / daysRequiredToComplete
      : (user.weekly_capacity ?? 0) / totalWorkdays;

  const dailyUtilizationRate = Math.floor(
    ((dailyWorkMinutesRequiredToCompleteByInitialDueDate * 60) / averageDailyCapacity) * 100
  );

  return {
    totalCapacityForRange,
    dailyWorkMinutesRequiredToCompleteWithinRange,
    dailyUtilizationRate,
    currentDailyUserCapacity,
    minutesOfWorkRemainingAsDays,
  };
}

interface ForecastAssigneeLight extends Omit<ForecastAssignee, 'estimated_minutes'> {
  estimated_minutes?: number;
}
export const generateForecastDraft = (
  params: Merge<
    Partial<Forecast>,
    { users: Array<ForecastAssigneeLight>; context: TaskAnalyticsContext }
  >
): ForecastDraft => {
  const {
    title = '',
    parent_title = '',
    id = -1,
    from = DateTime.now().toISODate(),
    to = DateTime.now().plus({ days: 10 }).toISODate(),
    estimated_minutes = 60 * 24 * 3,
    logged_duration = durationFromHours(0),
    planned_duration = durationFromHours(0),
    completed_at = null,
    users,
    label_ids = [],
    project = null,
  } = params;

  return {
    id,
    temporaryId: uuidv4(),
    title,
    parent_title,
    description: '',
    from,
    to,
    completed_at,
    logged_duration,
    estimated_minutes,
    planned_duration,
    project,
    label_ids,
    users,
    manage: true,
    updated_at: new Date().toISOString(),
    created_at: DateTime.now().toISO(), // Same format as API returns
    context: params.context,
  };
};

interface OptionFactoryMeta {
  /** Can the actor update this Forecast? */
  canUpdate?: boolean;
  /** Can the actor delete this forecast? */
  canDelete?: boolean;
  /** Show start/stop timer option? Only shows for non-completed Tasks */
  allowTimerOption?: boolean;
  /** Is there a time entry linked to this Forecast with a timer running? Forces `allowTimerOption: true` */
  hasRunningTimer?: boolean;
  /** Is there a time entry "today" that has this forecast.id as hour.forecast_id? */
  hasHourAssociation?: boolean;
  /** TIC metadata for external tool linking */
  tic?: TicMeta;
}

/**
 * Generate typical Task dropdown options for `PopoverDropdownList`
 */
export const forecastOptionsFactory = (
  forecast: EnrichedForecast,
  meta: OptionFactoryMeta
): Array<DropdownOption> => {
  const {
    canUpdate = true,
    canDelete = true,
    allowTimerOption = meta.hasRunningTimer || false,
    hasRunningTimer = false,
    hasHourAssociation = false,
    tic,
  } = meta;

  const options: Array<DropdownOption> = [
    {
      name: 'Edit',
      action: 'edit',
      faIcon: 'pencil',
    },
  ];

  if (canUpdate) {
    options.push({
      idRef: forecast.id,
      action: 'toggleCompleted',
      faIcon: forecast?.meta.isDone ? 'circle' : 'check-circle',
      name: forecast?.meta.isDone ? 'Mark Incomplete' : 'Complete task',
    });
  }

  if (allowTimerOption && !forecast.meta.isDone) {
    options.push({
      name: hasRunningTimer ? 'Pause timer' : hasHourAssociation ? 'Resume timer' : 'Start timer',
      action: 'toggleTimer',
      faIcon: hasRunningTimer ? 'pause' : 'clock-o',
      active: hasRunningTimer,
    });
  }

  if (canDelete) {
    const deleteOption: DropdownOption = {
      idRef: forecast.id,
      action: 'delete',
      faIcon: 'trash',
      name: 'Delete',
      confirm: true,
    };
    if (tic?.uri) {
      const toolName = tic.tool_id ? ucFirst(tic.tool_id) : 'external tool';
      deleteOption.confirmMessage = `This will only delete the task in Timely. The original in ${toolName} will not be affected.`;
    }
    options.push(deleteOption);
  }

  // Visit in external tool option (for TIC-synced tasks)
  if (tic?.external_url) {
    const toolName = tic.tool_id ? ucFirst(tic.tool_id) : 'external tool';
    options.push({
      separator: true,
    } as DropdownOption);
    const visitOption: DropdownOption = {
      name: `Visit in ${toolName}`,
      action: 'visitExternal',
      meta: { url: tic.external_url },
    };
    if (tic.icon) {
      visitOption.icon = React.createElement(TicMetaIcon, { ticMeta: tic, size: 'small' });
    } else {
      visitOption.faIcon = 'external-link';
    }
    options.push(visitOption);
  }

  return options;
};
